import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { Modal, Input, Select, Textarea, Empty, MarketBadge } from '../components/UI'
import { useAuth } from '../hooks/useAuth'
import { can } from '../lib/permissions'
import { MARKETS } from '../lib/constants'

const STATUS_COLORS = {
  Active:'var(--accent3)', Restricted:'#F59E0B', Disabled:'var(--text3)',
  Checkpoint:'#F59E0B', Suspended:'#F59E0B', Banned:'#EF4444',
  Unpublished:'var(--text3)', Limited:'#F59E0B',
}
function StatusBadge({ value }) {
  return <span style={{fontSize:11,fontWeight:700,color:STATUS_COLORS[value]||'var(--text)',whiteSpace:'nowrap'}}>{value||'—'}</span>
}

const LEVELS = {
  bm: {
    api:'business-managers', title:'Business Managers', icon:'🏢', parentKey:null,
    cols:[
      { key:'bm_name', label:'BM Name', strong:true },
      { key:'bm_id', label:'BM ID' },
      { key:'status', label:'Status', badge:'status' },
      { key:'notes', label:'Notes' },
    ],
    fields:[
      { key:'bm_name', label:'BM Name', type:'text' },
      { key:'bm_id', label:'BM ID', type:'text' },
      { key:'status', label:'Status', type:'select', options:['Active','Restricted','Disabled'] },
      { key:'notes', label:'Notes', type:'textarea' },
    ],
  },
  profile: {
    api:'fb-profiles', title:'Profiles', icon:'👤', parentKey:'bm_ref',
    cols:[
      { key:'profile_name', label:'Profile', strong:true },
      { key:'profile_id', label:'Profile ID' },
      { key:'uid', label:'UID' },
      { key:'password', label:'Password' },
      { key:'twofa', label:'2FA' },
      { key:'hotmail', label:'Hotmail' },
      { key:'pass_mail', label:'Pass Mail' },
      { key:'recovery_mail', label:'Recovery Mail' },
      { key:'proxy_ip', label:'Proxy IP' },
      { key:'status', label:'Status', badge:'status' },
      { key:'notes', label:'Notes' },
    ],
    fields:[
      { key:'profile_name', label:'Profile Name', type:'text' },
      { key:'profile_id', label:'Profile ID', type:'text' },
      { key:'uid', label:'UID', type:'text' },
      { key:'password', label:'Password', type:'text' },
      { key:'twofa', label:'2FA', type:'text' },
      { key:'hotmail', label:'Hotmail', type:'text' },
      { key:'pass_mail', label:'Pass Mail', type:'text' },
      { key:'recovery_mail', label:'Recovery Mail', type:'text' },
      { key:'proxy_ip', label:'Proxy IP', type:'text' },
      { key:'status', label:'Status', type:'select', options:['Active','Checkpoint','Suspended','Banned'] },
      { key:'notes', label:'Notes', type:'textarea' },
    ],
  },
  page: {
    api:'fb-pages', title:'Pages', icon:'📄', parentKey:'bm_ref',
    cols:[
      { key:'page_name', label:'Page Name', strong:true },
      { key:'market', label:'Market', badge:'market' },
      { key:'page_id', label:'Page ID' },
      { key:'status', label:'Status', badge:'status' },
      { key:'notes', label:'Notes' },
    ],
    fields:[
      { key:'page_name', label:'Page Name', type:'text' },
      { key:'market', label:'Market', type:'select', options:['', ...MARKETS] },
      { key:'page_id', label:'Page ID', type:'text' },
      { key:'status', label:'Status', type:'select', options:['Active','Restricted','Unpublished','Banned'] },
      { key:'notes', label:'Notes', type:'textarea' },
    ],
  },
  account: {
    api:'fb-ad-accounts', title:'Ad Accounts', icon:'💳', parentKey:'bm_ref',
    cols:[
      { key:'account_name', label:'Account Name', strong:true },
      { key:'market', label:'Market', badge:'market' },
      { key:'account_id', label:'Account ID' },
      { key:'status', label:'Status', badge:'status' },
      { key:'spend_limit', label:'Spend Limit' },
      { key:'currency', label:'Currency' },
      { key:'notes', label:'Notes' },
    ],
    fields:[
      { key:'account_name', label:'Account Name', type:'text' },
      { key:'market', label:'Market', type:'select', options:['', ...MARKETS] },
      { key:'account_id', label:'Account ID', type:'text' },
      { key:'status', label:'Status', type:'select', options:['Active','Limited','Disabled','Banned'] },
      { key:'spend_limit', label:'Spend Limit', type:'text' },
      { key:'currency', label:'Currency', type:'text' },
      { key:'notes', label:'Notes', type:'textarea' },
    ],
  },
}

export function FBInfrastructure() {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const perms = (typeof user?.permissions === 'object' && user?.permissions) ? user.permissions : {}
  const cEdit = isOwner || can(perms, 'fbinfra', 'edit')
  const cCreate = isOwner || can(perms, 'fbinfra', 'create')
  const cDelete = isOwner || can(perms, 'fbinfra', 'delete')

  const [tab, setTab] = useState('manage')
  const [overviewRows, setOverviewRows] = useState([])
  const [tree, setTree] = useState([])
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [stack, setStack] = useState([{ level:'bm', parent:null, parentLabel:'Business Managers' }])
  const current = stack[stack.length - 1]
  const cfg = LEVELS[current.level]

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.fbList(cfg.api, current.parent?.id)
      setRows(data)
    } catch (err) { toast.error(err.message); setRows([]) }
    setLoading(false)
  }, [cfg.api, current.parent])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (tab !== 'overview') return
    setOverviewLoading(true)
    api.fbTree().then(setTree).catch(e => { toast.error(e.message); setTree([]) }).finally(() => setOverviewLoading(false))
  }, [tab])

  function drillInto(row, targetLevel) {
    // BM now has three sibling children; targetLevel says which. Others don't drill.
    if (current.level !== 'bm' || !targetLevel) return
    const label = row[cfg.cols[0].key] || `#${row.id}`
    setStack(s => [...s, { level: targetLevel, parent: { id: row.id, name: label }, parentLabel: label }])
  }
  function goToCrumb(i) { setStack(s => s.slice(0, i + 1)) }

  function openAdd() {
    const init = {}
    cfg.fields.forEach(f => { init[f.key] = f.type==='select' ? (f.options.includes('Active')?'Active':'') : '' })
    if (cfg.api==='fb-ad-accounts') init.currency = 'EUR'
    setForm(init); setModal('add')
  }
  function openEdit(row) { setForm({ ...row }); setModal(row) }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  async function save() {
    setSaving(true)
    try {
      const body = {}
      cfg.fields.forEach(f => { body[f.key] = form[f.key] ?? '' })
      if (modal === 'add') {
        if (cfg.parentKey && current.parent) body[cfg.parentKey] = current.parent.id
        await api.fbCreate(cfg.api, body)
      } else {
        await api.fbUpdate(cfg.api, modal.id, body)
      }
      toast.success('Saved!'); setModal(null); load()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }
  async function delRow(row) {
    const childWarn = current.level !== 'account' ? ' This will also delete everything nested under it.' : ''
    if (!window.confirm(`Delete "${row[cfg.cols[0].key]||row.id}"?${childWarn} This cannot be undone.`)) return
    try { await api.fbDelete(cfg.api, row.id); toast.success('Deleted'); load() }
    catch (err) { toast.error(err.message) }
  }

  const childLabel = null  // BM uses three explicit buttons; other levels have no children

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className="toolbar">
        <div className="toolbar-left" style={{flexWrap:'wrap',gap:6}}>
          <span className="toolbar-title">🌐 FB Infrastructure</span>
          <div style={{display:'flex',gap:4,marginLeft:8}}>
            <button onClick={()=>setTab('manage')} style={{background:tab==='manage'?'var(--accent)':'var(--bg3)',color:tab==='manage'?'#fff':'var(--text3)',border:'1px solid var(--border2)',borderRadius:6,padding:'4px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}}>🗂 Manage</button>
            <button onClick={()=>setTab('overview')} style={{background:tab==='overview'?'var(--accent)':'var(--bg3)',color:tab==='overview'?'#fff':'var(--text3)',border:'1px solid var(--border2)',borderRadius:6,padding:'4px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}}>📊 Overview</button>
          </div>
          {tab==='manage' && <div style={{display:'flex',alignItems:'center',gap:4,flexWrap:'wrap'}}>
            {stack.map((st, i) => (
              <span key={i} style={{display:'flex',alignItems:'center',gap:4}}>
                {i>0 && <span style={{color:'var(--text3)',fontSize:11}}>›</span>}
                <button onClick={()=>goToCrumb(i)} style={{background:'none',border:'none',cursor:'pointer',fontSize:12,fontWeight:i===stack.length-1?700:500,color:i===stack.length-1?'var(--accent)':'var(--text3)',padding:'2px 4px'}}>
                  {LEVELS[st.level].icon} {st.parentLabel}
                </button>
              </span>
            ))}
          </div>}
          <span className="toolbar-sub">{tab==='overview' ? `${overviewRows.length} ad accounts` : `${rows.length} ${cfg.title.toLowerCase()}`}</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          {tab==='manage' && cCreate && <button className="btn btn-primary" onClick={openAdd}>+ Add {cfg.title.replace(/s$/,'')}</button>}
        </div>
      </div>

      {tab==='manage' && <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading..." />
          : rows.length === 0 ? <Empty icon={cfg.icon} title={`No ${cfg.title.toLowerCase()} yet`} sub={cCreate?`Click "Add ${cfg.title.replace(/s$/,'')}" to start.`:''} />
          : <table className="data-table"><thead>
              <tr>
                {cfg.cols.map(c => <th key={c.key}>{c.label}</th>)}
                <th style={{width:current.level==='bm'?340:120,textAlign:'center',position:'sticky',right:0,zIndex:6,background:'var(--bg3)',borderLeft:'2px solid var(--border2)'}}>Actions</th>
              </tr>
            </thead>
            <tbody>{rows.map(r => (
              <tr key={r.id}>
                {cfg.cols.map(c => (
                  <td key={c.key}>
                    {c.badge==='status' ? <StatusBadge value={r[c.key]} />
                      : c.badge==='market' ? (r[c.key]?<MarketBadge market={r[c.key]} />:'—')
                      : c.strong ? <strong>{r[c.key]||'—'}</strong>
                      : <span style={{fontSize:12}}>{r[c.key]||'—'}</span>}
                  </td>
                ))}
                <td style={{position:'sticky',right:0,zIndex:2,background:'var(--bg2)',borderLeft:'2px solid var(--border2)',padding:'6px 8px'}}>
                  <div style={{display:'flex',gap:4,justifyContent:'center',flexWrap:'wrap'}}>
                    {current.level==='bm' && <>
                      <button className="btn btn-secondary" style={{padding:'4px 8px',fontSize:11,whiteSpace:'nowrap'}} onClick={()=>drillInto(r,'profile')}>👤 Profiles</button>
                      <button className="btn btn-secondary" style={{padding:'4px 8px',fontSize:11,whiteSpace:'nowrap'}} onClick={()=>drillInto(r,'page')}>📄 Pages</button>
                      <button className="btn btn-secondary" style={{padding:'4px 8px',fontSize:11,whiteSpace:'nowrap'}} onClick={()=>drillInto(r,'account')}>💳 Ad Accounts</button>
                    </>}
                    {cEdit && <button className="btn btn-secondary" style={{padding:'4px 8px',fontSize:11}} onClick={()=>openEdit(r)}>✏️</button>}
                    {cDelete && <button className="btn btn-danger" style={{padding:'4px 8px',fontSize:11}} onClick={()=>delRow(r)}>🗑</button>}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>}
      </div>}
      {tab==='overview' && <div style={{flex:1,overflow:'auto',padding:'16px 20px'}}>
        {overviewLoading ? <Empty icon="⏳" title="Loading..." />
          : tree.length === 0 ? <Empty icon="📊" title="No business managers yet" sub="Add them in the Manage tab." />
          : <div style={{display:'flex',flexDirection:'column',gap:14}}>
            {tree.map(bm => (
              <div key={bm.id} style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
                <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',background:'var(--bg3)',borderBottom:'1px solid var(--border)'}}>
                  <span style={{fontSize:15}}>🏢</span>
                  <strong style={{fontSize:14,color:'var(--text)'}}>{bm.bm_name}</strong>
                  {bm.bm_id && <span style={{fontSize:11,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{bm.bm_id}</span>}
                  <span style={{marginLeft:'auto',fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>
                    {bm.profiles.length} profiles · {bm.pages.length} pages · {bm.accounts.length} accounts
                  </span>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:0}}>
                  {[['👤 Profiles', bm.profiles, p=>p.profile_name, null],
                    ['📄 Pages', bm.pages, p=>p.page_name, p=>p.market],
                    ['💳 Ad Accounts', bm.accounts, a=>a.account_name, a=>a.market]].map(([label, items, nameOf, marketOf], ci) => (
                    <div key={ci} style={{padding:'12px 16px',borderRight: ci<2?'1px solid var(--border)':'none'}}>
                      <div style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',marginBottom:8}}>{label} ({items.length})</div>
                      {items.length === 0 ? <span style={{fontSize:12,color:'var(--text3)'}}>—</span>
                        : <div style={{display:'flex',flexDirection:'column',gap:6}}>
                          {items.map(it => (
                            <div key={it.id} style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
                              {marketOf && marketOf(it) ? <MarketBadge market={marketOf(it)} /> : null}
                              <span style={{color:'var(--text)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{nameOf(it)}</span>
                              {it.status && it.status!=='Active' && <span style={{fontSize:9,color:'#F59E0B',fontWeight:700}}>{it.status}</span>}
                            </div>
                          ))}
                        </div>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>}
      </div>}

      {modal && <Modal title={`${modal==='add'?'Add':'Edit'} ${cfg.title.replace(/s$/,'')}`} onClose={()=>setModal(null)}
        footer={<><button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
        {cfg.fields.map(f => (
          f.type==='textarea' ? <Textarea key={f.key} label={f.label} value={form[f.key]||''} onChange={set(f.key)} />
          : f.type==='select' ? <Select key={f.key} label={f.label} options={f.options} value={form[f.key]||''} onChange={set(f.key)} />
          : <Input key={f.key} label={f.label} value={form[f.key]||''} onChange={set(f.key)} />
        ))}
      </Modal>}
    </div>
  )
}
